/**
 * User-visible copy for the flight amendment "select dates" flow.
 */
export const AMENDMENT_SELECT_DATES_PAGE = {
  DOCUMENT_TITLE: 'Cleartrip: Select date — flight amendment',
  META_DESCRIPTION: 'Select new travel dates for your booking',
} as const

export const AMENDMENT_SELECT_DATES = {
  SELECT_TRAVELLERS: 'Select travellers',
  SELECT_JOURNEYS: 'Select journeys',
  SEARCH_ALTERNATE_FLIGHTS: 'Search alternate flights',
  LOADING_TRAVELLER_DETAILS: 'Loading your trip…',
  API_ERROR_TITLE: 'Something went wrong',
  API_ERROR_BODY: 'We could not load your booking. Please try again in a moment.',
  NOT_ELIGIBLE_TITLE: 'Date change not available',
  CONTACT_US_TITLE: 'Contact us',
  NOT_ELIGIBLE_BODY: 'This booking is not eligible for online date change. For help, contact support.',
  NOT_ELIGIBLE_AFTER_CASE_HINT: 'Follow the steps in the popup to reach our support team.',
} as const

export const AMENDMENT_JOURNEY_DATE_LABELS = {
  NEW_DEPARTURE_FLOAT: 'New departure date',
  NEW_RETURN: 'New return date',
  OUTBOUND_PLACEHOLDER: 'Select new date',
} as const

export const AMENDMENT_PAX_DEFAULT_NAME = 'Traveller' as const

export const AMENDMENT_STEPPER = {
  SELECT_DATE: 'Select date',
  CHOOSE_FLIGHT: 'Choose flight',
  REVIEW_AND_PAY: 'Review & pay',
} as const

export const AMENDMENT_ACCESSIBILITY = {
  OPEN_CALENDAR: 'Open calendar',
} as const

export const AMENDMENT_GDS_UI = {
  PARTIAL_PAX_BANNER:
    'For this booking, date change applies to all travellers together. Need a different option?',
  PARTIAL_PAX_LINK: 'Contact us',
} as const

export const AMENDMENT_SPECIAL_RT = {
  ERROR_PREFIX: 'The trip cannot be modified partially online. Please click',
  HERE_LINK: 'here',
  ERROR_SUFFIX:
    'and a cleartripper will reach out to you to modify only the onward or return sector booking. If you wish to modify your entire booking, please select the passengers and dates for the onward and return sector to proceed',
  TRACKING_MSG: 'The trip cannot be modified partially online',
} as const

export const AMENDMENT_SELECT_DATES_ERRORS = {
  PAX_NOT_SELECTED: 'Please select at least one passenger to proceed',
  DEPART_DATE_NOT_SELECTED:
    'Please select the new departure date if you wish to modify your onward journey',
  RETURN_DATE_NOT_SELECTED:
    'Please select the new return date if you wish to modify your return journey',
  PAX_INFANT:
    'Every infant should be accompanied by at least 1 adult in the trip as per airline policy. Please make the passenger selection again to proceed.',
  PAX_REMAINING_INFANT:
    'Every infant should be accompanied by at least 1 adult in the original trip as per airline policy. Please make the passenger selection again to proceed.',
  PAX_CHILD:
    'Every child should be accompanied by at least 1 adult in the trip as per airline policy. Please make the passenger selection again to proceed.',
  PAX_REMAINING_CHILD:
    'Every child should be accompanied by at least 1 adult in the original trip as per airline policy. Please make the passenger selection again to proceed.',
} as const

export function getAmendmentErrorBannerTitle(count: number): string {
  const noun = count > 1 ? 'errors' : 'error'
  return `Oops, we found ${count} ${noun} in your form submission. Please fix the fields that are marked and try again`
}

export const AMENDMENT_DATE_WARNING = {
  PICK_ANOTHER_DATE: 'Pick another date',
  CONTINUE_BOOKING: 'Continue booking',
  SUBTEXT: 'If you are sure of the selected date, please continue',
  CLEARCHOICE_PLUS_LABEL: 'ClearChoice Plus',
  FLEXIFLY_LABEL: 'Flexifly',
  EZCANCEL_PLUS_LABEL: 'EZ Cancel Plus',
} as const

export function getAmendInverseDepartWarning(dateLabel: string): string {
  return `Your onward date: ${dateLabel} is greater than the return date`
}

export function getAmendInverseReturnWarning(dateLabel: string): string {
  return `Your return date: ${dateLabel} is earlier than the onward date`
}

export function getAmendFlexiflyBenefitWarning(productLabel: string): string {
  return `Changing to this date may affect your ${productLabel} benefits.`
}

export function getAmendVasBenefitWarning(productLabel: string): string {
  return `Your ${productLabel} benefits may not apply for the selected date.`
}

export const AMENDMENT_CASE_CREATION_POPUP = {
  BACK_TO_TRIP: 'Back to Trip',
} as const

export const AMENDMENT_MORE_OPTIONS = {
  TITLE: 'More options',
  CANCEL_FLIGHT: 'Cancel my flight',
  CANCEL_FLIGHT_SUB: 'Cancel this flight in 3 easy steps',
  CALCULATE_REFUND: 'Calculate my refund',
  CALCULATE_REFUND_SUB: 'See eligible refund amount',
} as const

export function getAmendmentTripCancelPath(tripId: string): string {
  const id = tripId?.trim() || ''
  return `/accounts/trips/${encodeURIComponent(id)}/cancel`
}
